/**
 * The campaign a signup came from.
 *
 * The Manager writes these onto the client's permanent acquisition columns (`initial_utm_source`
 * and friends), but only reads them from the register call — and by then the visitor has clicked
 * through a page or two and the query string is long gone. So they are taken from the URL on the
 * first page of the session and kept until the register call asks for them.
 *
 * This is per-account attribution captured at signup, not a person's lifetime first touch: someone
 * who comes back through a different campaign and signs up again is attributed to the new one.
 * Nothing is invented — a visitor who arrived without campaign parameters sends none.
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();const STORAGE_KEY = 'offsoo:attribution'

/**
 * URL parameter → the key the Manager's `ClientAttribution::fromRequest()` reads.
 *
 * This map and that method are the only place the two codebases agree on a name. A parameter
 * missing here is a Manager column that is silently always null, which is what happened to every
 * platform below Meta and TikTok until 2026-08-01.
 *
 * **All five UTMs, not three.** `utm_content` carries the creative and `utm_term` the placement
 * (Meta) or the matched keyword (search) — ADR 0038 in Creatives. posthog-js already stores all
 * five on the *person* through `save_campaign_params`; forwarding these two is what puts them on
 * the paying **client**, where MRR, plan and churn live. `utm_id` is deliberately absent: PostHog
 * does not store it and ADR 0038 dropped it from the convention.
 *
 * **Google is three keys, not one.** Since iOS 14.5 it sends `gbraid` (web-to-app) or `wbraid`
 * (app-to-web) *instead of* `gclid`, so a visitor carries exactly one of the three. Keeping only
 * `gclid` left every iOS click from Google Ads unattributable server-side.
 *
 * `msclkid` is Microsoft's, and the only key its server-side conversion import accepts — it has no
 * shared-event-id mechanism the way Meta does. `li_fat_id` is LinkedIn's first-party id.
 *
 * `twclid` (X) and `epik` (Pinterest) are captured into PostHog by `custom_campaign_params` but are
 * deliberately not forwarded: neither platform has a pixel configured here, so the Manager would be
 * storing something nothing reads.
 */
const PARAMS = {
  utm_source: 'utmSource',
  utm_medium: 'utmMedium',
  utm_campaign: 'utmCampaign',
  utm_content: 'utmContent',
  utm_term: 'utmTerm',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  fbclid: 'fbclid',
  ttclid: 'ttclid',
  msclkid: 'msclkid',
  li_fat_id: 'liFatId',
} as const

export type Attribution = Partial<Record<(typeof PARAMS)[keyof typeof PARAMS], string>>

/** The column is 255 wide; a longer value would be truncated server-side anyway. */
const LIMIT = 255

function read(): Attribution {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as Attribution) : {}
  } catch {
    // Private modes and storage-blocking extensions throw rather than return null.
    return {}
  }
}

/**
 * Remembers the campaign parameters in the current URL, once per session.
 *
 * First touch wins: a visitor who lands on a campaign URL and then browses the site keeps the
 * campaign they arrived through, because the pages after it carry no parameters at all.
 */
export function captureAttribution(search: string = typeof window === 'undefined' ? '' : window.location.search) {
  if (typeof window === 'undefined' || Object.keys(read()).length > 0) {
    return
  }

  const params = new URLSearchParams(search)
  const found: Attribution = {}

  for (const [param, key] of Object.entries(PARAMS)) {
    const value = params.get(param)?.trim()
    if (value) {
      found[key] = value.slice(0, LIMIT)
    }
  }

  if (Object.keys(found).length === 0) {
    return
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(found))
  } catch {
    // Not being able to store it is not worth failing a signup over.
  }
}

/** What this session arrived with, to be sent along with the register call. Empty when unknown. */
export function attributionForSignup(): Attribution {
  return typeof window === 'undefined' ? {} : read()
}
